import { uniq } from 'lodash';
import { z } from 'zod';
import {
  exampleFacebookPixel,
  exampleFBConnectedInstagramAccount,
  FacebookPixel,
  FBConnectedInstagramAccount,
} from '../facebook';
import {
  exampleFunnelStageConfig,
  FunnelStageConfigDTO,
} from '../funnel/FunnelStageConfig';
import {
  AttrWindowsBySourceTypeDTO,
  exampleAttrWindowsBySourceType,
} from '../insights/Insights';
import {
  BusinessBranchOptionDTO,
  exampleBusinessBranchOption,
} from './BusinessBranchOption';
import { BusinessUserDTO, exampleBusinessUser } from './BusinessUser';

export type LeadWebhookFormat = z.infer<typeof LeadWebhookFormatDTO>;
const LeadWebhookFormatDTO = z.union([
  z.literal('off'),
  z.literal('otf'),
  z.literal('generic'),
  z.literal('hubspot'),
  z.literal('manduu'),
  z.literal('hotworx'),
]);

const MUST_BE_A_NUMBER = 'Must be a number';

export const BusinessConfigDTO = z.object({
  id: z
    .union([z.number(), z.string()])
    .transform((_) => Number(_))
    .optional(),
  error: z.union([z.string(), z.null()]).optional(),
  main: z.object({
    business_name: z.string().max(255, 'Business Name too Long'),
    website: z
      .string()
      .url('Website must be a valid url (http:// or https://)'),
    fb_account_id: z.string().regex(/^\d+$/, MUST_BE_A_NUMBER),
    fb_page_id: z.union([
      z.string().regex(/^\d+$/, MUST_BE_A_NUMBER),
      z.null(),
    ]),
    yearly_value: z.union([z.number(), z.string()]).transform((_) => Number(_)),
    start: z
      .string()
      .regex(
        /^\d{4}-\d{2}-\d{2}$/i,
        'Please specify the proper format of the date, YYYY-MM-DD',
      ),
    branch_id: z.number(),
    branch_name: z.string().optional(),
    pod_id: z.number(),
    pod_number: z.number().optional(),
    fb_dataset_id: z
      .union([
        z.number(),
        z.string().regex(/^\d+$/, 'Must be a number'),
        z.null(),
      ])
      .transform((_) => (!!_ ? `${_}` : null)),
    offline_event_set_id: z
      .union([
        z.number(),
        z.string().regex(/^\d+$/, 'Must be a number'),
        z.null(),
      ])
      .transform((_) => (!!_ ? `${_}` : null)),
    instagram_id: z.union([z.string(), z.null()]),
    grade_values: z.unknown(),
    yvc_party_size: z
      .union([z.number(), z.string()])
      .transform((_) => Number(_)),
    yvc_visits: z.union([z.number(), z.string()]).transform((_) => Number(_)),
    yvc_spend: z.union([z.number(), z.string()]).transform((_) => Number(_)),
    ingest_access_token: z.union([z.string(), z.null()]).optional(),
    close_percentage: z.number().max(100),
    timezone: z.string(),
    attr_windows: AttrWindowsBySourceTypeDTO,
    show_roi: z.boolean(),
    pixel_indicator: z.string(),
    pixel_indicator_desc: z.string(),
    data_indicator: z.string(),
    data_indicator_desc: z.string(),
    upload_indicator: z.string(),
    upload_indicator_desc: z.string(),
    phase: z.number(),
    lead_webhook_format: LeadWebhookFormatDTO,
    currency_type: z.string(),
    locale: z.string(),
    lookback_window: z.number(),
    override_yvc: z.boolean(),
    data_uploader_active: z.boolean(),
    reports_page_active: z.boolean(),
    received_product_demo: z.boolean(),
    repeat_check_fullname_fallback: z.boolean(),
    allow_lead_collection: z.boolean(),
    mktg_retainer: z
      .union([z.number(), z.string()])
      .transform((_) => Number(_)),
    monthly_spend: z
      .union([z.number(), z.string()])
      .transform((_) => Number(_)),
    ga_view_id: z
      .union([z.string(), z.number(), z.null()])
      .transform((_) => `${_}`),
    client_customer_id: z
      .union([z.string(), z.number(), z.null()])
      .transform((_) => `${_}`),
    goal_text: z.union([z.string(), z.null()]),
    first_initial_purchase: z
      .union([z.number(), z.string()])
      .transform((_) => Number(_)),
    city: z.union([z.string(), z.null()]),
    state: z.union([z.string(), z.null()]),
    fb_ad_authorizer_id: z.union([z.number(), z.null()]).optional(),
    fb_page_authorizer_id: z.union([z.number(), z.null()]).optional(),
    fb_leads_polling_form_ids: z
      .union([z.array(z.string()), z.null()])
      .optional(),
    address: z.string().nullish(),
    latitude: z.string().nullish(), // while stored in the db as a number, returned as a string due to precision, but this does not affect our functionality
    longitude: z.string().nullish(), // while stored in the db as a number, returned as a string due to precision, but this does not affect our functionality
    google_type: z.string().nullish(),
    google_looker_embed_url: z.string().nullish(),
    google_ads_customer_id: z.string().nullish(),
    google_ads_manager_id: z.string().nullish(),
    sf_account_id: z.string().nullish(),
    slack_channel_id: z.string().nullish(),
    google_drive_folder_id: z.string().nullish(),
    google_authorizer_id: z.number().nullish(),
    franchise_id: z.number().nullish(),
    ownership_group_id: z.number().nullish(),
    fb_builder: z.boolean(),
    adaccount_system_user_id: z.string().nullable(),
    page_system_user_id: z.string().nullable(),
    dataset_system_user_id: z.string().nullable(),
    offline_system_user_id: z.string().nullable(),
    instagram_system_user_id: z.string().nullable(),
    fb_account_name: z.string().nullable(),
    fb_page_name: z.string().nullable(),
    fb_dataset_name: z.string().nullable(),
    fb_offline_name: z.string().nullable(),
    instagram_name: z.string().nullable(),
  }),
  funnel: z.object({
    active: z.union([z.number(), z.null()]),
    preset: z.number(),
    stages: z
      .array(FunnelStageConfigDTO)
      .refine(
        (funnelStageConfig) =>
          uniq(funnelStageConfig.map((fs) => fs.conversion_name)).length ===
          funnelStageConfig.length,
        {
          message: 'Must have unique Conversion Names for each Funnel Stage',
        },
      ),
  }),
  users: z.array(BusinessUserDTO),
  options: z.array(BusinessBranchOptionDTO),
  tags: z.array(z.number()),
  dataSources: z.array(z.number()),
});

export type BusinessConfig = z.TypeOf<typeof BusinessConfigDTO>;

export const exampleBusinessConfig: BusinessConfig = {
  main: {
    business_name: 'New Business',
    website: 'http://example.com',
    fb_account_id: '11111',
    fb_page_id: '22222',
    mktg_retainer: 1020,
    yearly_value: 1040,
    start: '2021-01-01',
    branch_id: 1,
    pod_id: 1,
    fb_dataset_id: '1242',
    instagram_id: '33333',
    offline_event_set_id: '1234',
    monthly_spend: 500,
    ga_view_id: '123456789',
    client_customer_id: '405239',
    goal_text: 'You have reached your goal!',
    yvc_party_size: 2,
    yvc_visits: 3,
    yvc_spend: 100,
    grade_values: {},
    ingest_access_token: 'nest_efjibneijbu23ro2i3n2',
    close_percentage: 10,
    timezone: 'America/New_York',
    attr_windows: exampleAttrWindowsBySourceType,
    show_roi: true,
    pixel_indicator: 'Pixel Activity',
    pixel_indicator_desc: 'Pixel Desc',
    data_indicator: 'Data Activity',
    data_indicator_desc: 'Pixel Desc',
    upload_indicator: 'Upload Activity',
    upload_indicator_desc: 'Upload Desc',
    first_initial_purchase: 100,
    phase: 1,
    city: 'New York City',
    state: 'New York',
    lead_webhook_format: 'off',
    currency_type: 'USD',
    locale: 'en-US',
    lookback_window: 90,
    override_yvc: true,
    data_uploader_active: true,
    reports_page_active: true,
    received_product_demo: true,
    repeat_check_fullname_fallback: true,
    allow_lead_collection: true,
    fb_ad_authorizer_id: null,
    fb_page_authorizer_id: null,
    address: null,
    longitude: null,
    latitude: null,
    google_type: null,
    google_looker_embed_url: null,
    google_ads_customer_id: null,
    google_ads_manager_id: null,
    google_drive_folder_id: null,
    google_authorizer_id: null,
    sf_account_id: 'SF-001',
    slack_channel_id: 'C01234567',
    franchise_id: null,
    ownership_group_id: null,
    fb_builder: false,
    adaccount_system_user_id: null,
    page_system_user_id: null,
    dataset_system_user_id: null,
    offline_system_user_id: null,
    instagram_system_user_id: null,
    fb_account_name: null,
    fb_page_name: null,
    fb_dataset_name: null,
    fb_offline_name: null,
    instagram_name: null,
  },
  funnel: {
    active: null,
    preset: 3,
    stages: [exampleFunnelStageConfig],
  },
  users: [exampleBusinessUser],
  options: [exampleBusinessBranchOption],
  tags: [],
  dataSources: [],
};

// DTO / Interfaces for retrieving objects connected to FB Ad Account
export const BusinessFBObjectsDTO = z.object({
  fb_account_id: z.string(),
  business_id: z
    .union([z.number(), z.string()])
    .transform((_) => Number(_))
    .optional(),
  fb_ad_authorizer_id: z.union([z.number(), z.null()]).optional(),
});

export const exampleBusinessFBObjectsConfig: BusinessFBObjectsConfig = {
  fb_account_id: '12341236',
};

export type BusinessFBObjectsConfig = z.TypeOf<typeof BusinessFBObjectsDTO>;

export interface BusinessFBObjects {
  pixels: FacebookPixel[];
  instagrams: FBConnectedInstagramAccount[];
}

export const exampleBusinessFBObjects: BusinessFBObjects = {
  pixels: [exampleFacebookPixel],
  instagrams: [exampleFBConnectedInstagramAccount],
};
